import React, { memo, useEffect, useReducer, useState } from 'react';
import {
  AdvanceFilter,
  Button,
  Input,
  Modal,
  SelectBox,
  Table,
  Text,
} from 'components/src';
import {
  routesName,
  useDeleteAdminDeleteUser,
  useGetAdminExportUsers,
  useGetAdminUsersList,
  useNavigation,
} from 'core';
import { useTranslation } from 'react-i18next';
import { Flex, Space, TablePaginationConfig } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CiEdit } from 'react-icons/ci';
import { PiNotebookLight } from 'react-icons/pi';
import {
  handleFiltering,
  handleModal,
  initialState,
  reducer,
  setId,
  updateCurrentPage,
} from './store';
import { RiDeleteBin6Line, RiFileExcel2Line } from 'react-icons/ri';
import { Button as ButtonAnt } from 'antd';
import { downloadFile } from 'core';

const UsersList = memo(() => {
  const { _navigate, _navigateWithParams } = useNavigation();
  const { i18n } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
    []
  );
  const [{ currentPage, id, showModal, advanceFilter }, dispatch] =
    useReducer(reducer, initialState);

  const { mutate, isPending } = useDeleteAdminDeleteUser({
    onSuccess: () => {
      refetch();
    },
  });

  const [isStatisticDownload, setStatisticDownload] = useState(false);
  const {
    data: excelData,
    isLoading: exelLoading,
    isFetched,
  } = useGetAdminExportUsers(
    {
      enabled: isStatisticDownload,
    },
    {
      responseType: 'blob',
    }
  );

  const handleDownload = () => {
    setStatisticDownload(true);
  };

  useEffect(() => {
    if (excelData && isStatisticDownload) {
      downloadFile('users', excelData as Blob);
      setStatisticDownload(false);
    }
  }, [excelData, isStatisticDownload]);

  const [sortOption, setSortOption] = useState('');
  const filterArray = [
    { label: 'ایجاد شدن', value: 'created_at' },
    { label: 'آپدیت شدن', value: 'updated_at' },
    { label: 'وضعیت', value: 'status' },
  ];

  const { data, isFetching, refetch } = useGetAdminUsersList({
    pageNumber: currentPage,
    pageSize: '10',
    ...(advanceFilter.mobileNumber && {
      mobileNumber: advanceFilter.mobileNumber,
    }),
    ...(advanceFilter.nameAndFamily && {
      nameAndFamily: advanceFilter.nameAndFamily,
    }),
    ...(advanceFilter.userName && {
      userName: advanceFilter.userName,
    }),
    ...(sortOption && {
      sortColumn: sortOption,
      sortDirection: 'DESC',
    }),
  });

  useEffect(() => {}, [refetch]);

  const columns: any = [
    {
      title: i18n.t('id'),
      key: 'id',
      dataIndex: 'id',
      render: (text: any) => <Text>{text}</Text>,
    },
    {
      title: i18n.t('nameAndFamily'),
      dataIndex: 'nameAndFamily',
      key: 'nameAndFamily',
      render: (text: any) => <Text>{text}</Text>,
    },
    {
      title: i18n.t('userName'),
      dataIndex: 'userName',
      key: 'userName',
      render: (text: any) => <Text>{text}</Text>,
    },
    {
      title: i18n.t('mobileNumber'),
      dataIndex: 'mobileNumber',
      key: 'mobileNumber',
      render: (text: any) => <Text>{text}</Text>,
    },
    {
      title: i18n.t('role'),
      dataIndex: 'role',
      key: 'role',
      render: (text: any) =>
        text?.map((item: any) => {
          return <Text>{item.name}-</Text>;
        }),
    },
    {
      title: i18n.t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (text: any) => <Text>{text}</Text>,
    },
    {
      title: i18n.t('isActive'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (text: any) => (
        <Text>{text ? i18n.t('active') : i18n.t('deActive')}</Text>
      ),
    },
    {
      title: i18n.t('category'),
      dataIndex: 'category',
      key: 'category',
      render: (text: any) => <Text>{text?.name}</Text>,
    },
    {
      title: i18n.t('operation'),
      key: 'operation',
      dataIndex: 'operation',
      render: (_: any, record: any) => (
        <Flex align="center" justify="start">
          <Space>
            <Button
              title={i18n.t('details')}
              customColor="warning"
              onClick={() => {
                _navigateWithParams(routesName.USERDETAIL, {
                  id: record.id,
                });
              }}
            >
              <PiNotebookLight size={20} />
            </Button>
            <Button
              title={i18n.t('edit')}
              onClick={() => {
                _navigateWithParams(routesName.EDITUSER, {
                  id: record.id,
                });
              }}
            >
              <CiEdit size={20} />
            </Button>

            <Button
              title={i18n.t('delete')}
              customColor="danger"
              onClick={() => {
                dispatch(handleModal(true));
                dispatch(setId(record.userName));
              }}
              disabled={isPending}
            >
              <RiDeleteBin6Line size={20} />
            </Button>
          </Space>
        </Flex>
      ),
    },
  ];

  const onAdvanceFilterChange = (filterValues: any) => {
    dispatch(handleFiltering(filterValues));
  };

  const handlePageChange = (current_page: number) => {
    dispatch(updateCurrentPage(current_page));
  };

  const pagination: TablePaginationConfig = {
    current: data?.meta?.current_page,
    pageSize: 10,
    total: data?.data?.totalCount,
    onChange: handlePageChange,
    showSizeChanger: false,
    position: ['bottomRight'],
  };
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <>
      <Modal
        text={i18n.t('AreYouSureYouWantToDelete')}
        id={id}
        open={showModal}
        onOk={() => {
          mutate({
            queryParams: {
              userName: id,
            },
          });

          dispatch(handleModal(false));
        }}
        onCancel={() => dispatch(handleModal(false))}
      />
      <Button
        type="primary"
        onClick={() => {
          _navigate(routesName.ADDUSER);
        }}
      >
        <PlusOutlined />
        {i18n.t('addUser')}
      </Button>

      <SelectBox
        allowClear
        label={i18n.t('مرتب سازی')}
        options={filterArray}
        placeholder={i18n.t('مرتب سازی')}
        onChange={(e: any) => {
          setSortOption(e);
          refetch();
        }}
      />

      <Table
        rowSelection={rowSelection}
        loading={isFetching}
        filterComponent={
          <AdvanceFilter
            setAdvanceFilter={onAdvanceFilterChange}
            nameAndFamilyField={true}
            userNameField={true}
            mobileNumberField={true}
          />
        }
        excelIcon={
          <ButtonAnt
            style={{
              backgroundColor: 'green',
              display: 'flex',
              color: '#fff',
              border: 'none',
            }}
            loading={exelLoading}
            onClick={handleDownload}
          >
            <RiFileExcel2Line size={25} />
            {i18n.t('export')}
          </ButtonAnt>
        }
        {...{ columns }}
        dataSource={data?.data.users}
        pagination={pagination}
      ></Table>
    </>
  );
});
export { UsersList };
